import React, { FC, useCallback, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Grid, Autocomplete, TextField, CircularProgress, Theme } from '@mui/material'
import Typography from '@mui/material/Typography'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles } from '@mui/styles'
import clsx from 'clsx'
import { Modal } from '../../../components/Modal'
import { IData } from '../UserProfileTabs'
import { introducerReassignResolver } from './resolvers'
import { getUsersForReassignData, usersForReassignIsLoading } from '../../../store/selectors'
import { fetchUsersForReassignRequest, reassignIntroducerUserRequest } from '../../../store/actions'

const useStyles = makeStyles((theme: Theme) => ({
  select: {
    color: theme.palette.primary.light,
    '&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.secondary.main,
    },
    '& .MuiAutocomplete-endAdornment': {
      '& .MuiIconButton-root': {
        color: theme.palette.primary.light,
      },
    },
  },
  disabled: {
    color: theme.palette.primary.light,
    pointerEvents: 'none',
    '& .MuiOutlinedInput-root': {
      cursor: 'default',
    },
    '& .MuiAutocomplete-endAdornment': {
      display: 'none',
    },
  },
  noOptions: {
    color: theme.palette.primary.light,
    padding: theme.spacing(2),
  },
  anim: {
    color: '#6BFFBF',
    animationDuration: '550ms',
  },
  circle: {
    strokeLinecap: 'round',
  },
}))

interface IntroducerReassignModalProps extends Pick<IData, 'idSf'> {
  introducerId?: IData['id']
  isOpen: boolean
  modalClose: () => void
  reassignId: string | null
}

export const IntroducerReassignModal: FC<IntroducerReassignModalProps> = ({
  isOpen,
  modalClose,
  idSf,
  introducerId,
  reassignId,
}) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const loading = useSelector(usersForReassignIsLoading)
  const items = useSelector(getUsersForReassignData)
  const filteredItems = useMemo(() => items.filter((user) => user.id !== idSf), [items, idSf])

  const formMethods = useForm({
    mode: 'all',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    resolver: introducerReassignResolver,
  })
  const {
    reset,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = formMethods

  const selectedUser = watch('reassignUser')

  const onSubmit = useCallback(
    (data) => {
      if (introducerId && reassignId)
        dispatch(
          reassignIntroducerUserRequest({
            introducerId,
            referredUserId: +reassignId,
            newIntroducerId: data.reassignUser,
          }),
        )
      reset?.()
      modalClose?.()
    },
    [reset, modalClose, dispatch, introducerId, reassignId],
  )

  const fetchUsersForReassign = useCallback(() => {
    dispatch(fetchUsersForReassignRequest())
  }, [dispatch])

  return (
    <Modal
      title={'Reassign user'}
      isOpen={isOpen}
      modalClose={modalClose}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitDisabled={!selectedUser}
      width={524}
    >
      <FormProvider {...formMethods}>
        <Grid container direction="column" gap={2}>
          <Grid item xs={12} display="flex" flexDirection="row" justifyContent="center" alignItems="center">
            <Grid item xs={4}>
              <Typography variant="h6">Current Introducer</Typography>
            </Grid>
            <Grid item xs={8}>
              <Autocomplete
                value={idSf}
                disableClearable
                options={idSf ? [idSf] : []}
                getOptionLabel={(option) => option}
                renderInput={(params) => (
                  <TextField {...params} className={clsx(classes.select, classes.disabled)} fullWidth />
                )}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} display="flex" flexDirection="row" justifyContent="center" alignItems="center">
            <Grid item xs={4}>
              <Typography variant="h6">New Introducer</Typography>
            </Grid>
            <Grid item xs={8}>
              <Autocomplete
                loading={loading}
                loadingText={
                  <CircularProgress
                    variant="indeterminate"
                    disableShrink
                    className={classes.anim}
                    classes={{
                      circle: classes.circle,
                    }}
                    size={24}
                    thickness={4}
                  />
                }
                onOpen={fetchUsersForReassign}
                options={filteredItems}
                noOptionsText={'User not found'}
                getOptionLabel={(option) => `${option.name} (${option.id})`}
                isOptionEqualToValue={(option, value) => option.id === (value?.id || value)}
                value={filteredItems.find((user) => user.id === selectedUser) || null}
                onChange={(_, value) => setValue('reassignUser', value ? value.id : null)}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.name} ({option.id})
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    className={classes.select}
                    placeholder="Select user"
                    error={!!errors.reassignUser}
                    helperText={errors.reassignUser?.message}
                    aria-label="Select user"
                  />
                )}
                classes={{
                  noOptions: classes.noOptions,
                }}
                fullWidth
              />
            </Grid>
          </Grid>
        </Grid>
      </FormProvider>
    </Modal>
  )
}
